import React from 'react';
import { get, isNil, isEmpty, filter, isUndefined } from 'lodash';
import { withRouter } from 'react-router-dom';

// React view component to render
import { SERVICE_RESPONSE_STATUS, SIZES } from 'constants/common';
import { memberDetailsPayBalanceStepsWidget as MemberDetailsPayBalanceStepsWidgetView } from 'ui/views/widgets/memberDetailsPayBalanceStepsWidget';
import { MemberDetailsPayBalanceStepsWidgetService } from 'services/memberDetailsPayBalanceStepsWidget';
import { DASHBOARD_SUB_ROUTES } from 'constants/route-paths';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { getAppSyncData } from 'utils/getAppSyncData';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberDetailsPayBalanceStepsWidget } from 'ui/containers/MemberDetailsPayBalanceStepsWidget';
 *
 * // render
 * <MemberDetailsPayBalanceStepsWidget propName='propValue'/>
 */
class _MemberDetailsPayBalanceStepsWidget extends React.Component {

	constructor( props ) {
		super( props );
		
		// bind methods
		this.handleNavActionClick = this.handleNavActionClick.bind( this );
		this.handleAmountChange = this.handleAmountChange.bind( this );
		this.handlePaymentOptionSelect = this.handlePaymentOptionSelect.bind( this );
		this.handleSuccessfullPayment = this.handleSuccessfullPayment.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
			componentData: undefined,
			successAcknowledgementData: null,
			displaySuccessAcknowledgement: false,
			amountSelection: {
				isValid: false,
				selection: [],
			},
			paymentSelection: {
				isValid: false,
				selection: null,
			},
        };

		this.data = {
			memberId: isUndefined( this.props.location.state ) ? getAppSyncData( 'memberId' ) : get( this.props.location, 'state.memberId', undefined )
		};

        // create service instance
        this.memberDetailsPayBalanceStepsWidgetService = new MemberDetailsPayBalanceStepsWidgetService();
        
        // start component data fetch
        this.fetchComponentData();

    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'MemberDetailsPayBalanceStepsWidget.fetchComponentData()' );

        // cancel ongoing request
        this.memberDetailsPayBalanceStepsWidgetService.cancel();

        // get status and service data
        const { status, data } = await this.memberDetailsPayBalanceStepsWidgetService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
	}
	
	/**
	 * @desc handle navigation action click
	 */
	handleNavActionClick( action = {} ) {
		log.debug( 'MemberDetailsPayBalanceStepsWidget.handleNavActionClick()' );
		
		if( 'back' === action.name ) {
			
			// go to member management page
			this.props.history.push( {
				pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
				state: { memberId: this.data.memberId }
			} );
		} else if( 'cancel' === action.name ) {

			// open memeber management page
			this.props.history.push( {
				pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
				state: { memberId: this.data.memberId }
			} );
		} else if( 'payNow' === action.name ) {
			this.submitSelectionData( action );
		}
	}

	/**
	 * @desc Save data
	 */
	async submitSelectionData( action = {} ) {
		log.debug( 'MemberDetailsPayBalanceStepsWidget.submitSelectionData()' );

		// cancel ongoing request
		this.memberDetailsPayBalanceStepsWidgetService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.memberDetailsPayBalanceStepsWidgetService.submitSelectionData( {
			url: action.url,
			data: {
				context: {
					memberId: this.data.memberId,
				},
				data: {
					amountSelection: filter( get( this.state, 'amountSelection.selection', [] ), member => ! isEmpty( member.amount ) ),
					paymentSelection: get( this.state, 'paymentSelection.selection', null ),
				}
			}
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				successAcknowledgementData: data,
				displaySuccessAcknowledgement: true,
			} );
		} else {

			// show error dialog
			if ( ! isEmpty( data.description ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
			} else {
				errorOverlay.open();
			}
		}
	}

	/**
	 * @desc When amount changes
	 */
	handleAmountChange( { isValid = false, selection = [] } = {} ) {
		log.debug( 'MemberDetailsPayBalanceWidget.handleAmountChange()' );

		this.setState( {
			amountSelection: {
				isValid: isValid,
				selection: selection,
			}
		} );
	}

	/**
	 * @desc When payment option is selected
	 */
	handlePaymentOptionSelect( { id = null } = {} ) {
		log.debug( 'MemberDetailsPayBalanceWidget.handlePaymentOptionSelect()' );

		this.setState( {
			paymentSelection: {
				isValid: ! isNil( id ),
				selection: id,
			}
		} );
	}

	/**
	 * @desc When payment is completed
	 */
	handleSuccessfullPayment() {
		log.debug( 'MemberDetailsPayBalanceWidget.handleSuccessfullPayment()' );

		// open memeber management page
		this.props.history.push( {
			pathname: DASHBOARD_SUB_ROUTES.MEMBER_MANAGEMENT,
			state: { memberId: this.data.memberId }
		} );
	}

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.memberDetailsPayBalanceStepsWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {

        return (
            <MemberDetailsPayBalanceStepsWidgetView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
				{ ...get( this.state, 'componentData', undefined  ) }
				onNavActionClick={ this.handleNavActionClick }
				onAmountChange={ this.handleAmountChange }
				onPaymentOptionSelect={ this.handlePaymentOptionSelect }
				disableSubmitAction={ ! this.state.amountSelection.isValid || ! this.state.paymentSelection.isValid }
				successAcknowledgementData={ this.state.successAcknowledgementData }
				displaySuccessAcknowledgement={ this.state.displaySuccessAcknowledgement }
				onSuccessfullPayment={ this.handleSuccessfullPayment }
				requestPayload={ {
					context: {
						memberId: this.data.memberId,
					}
				} }
				paymentSelectionId={ this.state.paymentSelection.selection }
            />
        );
    }
}

// set display name
_MemberDetailsPayBalanceStepsWidget.displayName = '_MemberDetailsPayBalanceStepsWidget';

// set default props
_MemberDetailsPayBalanceStepsWidget.defaultProps = {};

// prop types
_MemberDetailsPayBalanceStepsWidget.propTypes = {};

/*********************************************/

// wrap with router
export const MemberDetailsPayBalanceStepsWidget = withRouter( _MemberDetailsPayBalanceStepsWidget );
